import type { WideAreaAIVariant } from '@/components/WideAreaAICTA';

/**
 * Wide Area AI funnel registry — "SaaS funnel" KB pages.
 *
 * KB articles listed here replace the consulting ContextualServiceCTA with a
 * Wide Area AI product CTA (wideareaai.com) in the KB sidebar. A page is
 * either a funnel page or an ads page (consumer-pages.ts), never both — and a
 * page lives in exactly one SaaS funnel (these slugs were carved out of
 * GLITCHREPLAY_KB_FUNNEL because local-GPU / rate-limit intent beats
 * error-tracking intent on them).
 *
 * Source cluster: the AI-coding-assistant KB articles where the reader is
 * explicitly dealing with model selection, local models, or API rate limits —
 * exactly who a local-first AI gateway serves.
 *
 * Keyed by `${category}/${slug}`.
 */
export const WIDEAREAAI_KB_FUNNEL: Record<string, WideAreaAIVariant> = {
  // Local-model intent — the bullseye audience: already running (or trying to
  // run) models on their own hardware.
  'openai/how-to-use-local-models': 'local-models',

  // Codex CLI is OpenAI-compatible — it can point straight at a Wide Area
  // Intelligence gateway (custom base URL) and route to the GPU you own. These
  // are the highest-fit AI-CLI entry pages after local-models. (We deliberately
  // do NOT funnel Claude Code / Copilot / Gemini CLI install pages here — those
  // clients aren't OpenAI-compatible, so "route this to your own GPU" wouldn't
  // be true for that reader. They stay in GLITCHREPLAY_KB_FUNNEL.)
  'openai/how-to-install-codex-cli': 'local-models',

  // Custom base URL — the literal mechanism a Wide Area AI gateway relies on:
  // Codex (OpenAI-compatible) pointed at a custom /v1 endpoint that routes to
  // the GPU you own. Highest-fit explainer for the product, so route to the
  // local-inference variant.
  'openai/how-to-use-custom-base-url': 'local-models',

  // Serving Qwen-Coder as an OpenAI-compatible endpoint — reader is literally
  // standing up local inference for their CLI; the bullseye local-models intent.
  'openai/how-to-serve-qwen-coder-as-openai-endpoint': 'local-models',

  // Model-switching / routing intent — readers comparing or routing between
  // models, i.e. exactly who'd put a gateway in front of their inference.
  'claude/how-to-switch-models': 'local-models',
  'gemini/how-to-switch-models': 'local-models',
  'openai/how-to-switch-models': 'local-models',
  'gemini/how-to-fix-model-switching': 'local-models',

  // Token-cost intent — running Codex over large diffs/reviews burns API
  // tokens; route to a GPU you own (OpenAI-compatible) to cut the bill.
  'openai/how-to-use-codex-for-code-review': 'token-costs',

  // Token-cost intent — reader is explicitly trying to cut their OpenAI API
  // bill; routing routine requests to a GPU you own (OpenAI-compatible) is the
  // product's core cost pitch.
  'openai/how-to-reduce-api-token-costs': 'token-costs',

  // Rate-limit intent — "never hit a rate limit on your own GPU".
  'claude/how-to-fix-rate-limits': 'rate-limits',
};

/** Variant for a KB article, or undefined when the article isn't in the funnel. */
export function getWideAreaAIVariantForKB(
  category: string,
  slug: string
): WideAreaAIVariant | undefined {
  return WIDEAREAAI_KB_FUNNEL[`${category}/${slug}`];
}

/**
 * Wide Area AI funnel registry — blog-post variant.
 *
 * Blog posts listed here render a Wide Area AI product CTA (wideareaai.com) in
 * the blog sidebar / end-of-article instead of the consulting
 * ContextualServiceCTA. Alert24 and PlanetRoadmap take precedence (resolved
 * first in app/blog/[slug]/page.tsx); these slugs are chosen to NOT overlap
 * those funnels, the consumer-ads list, or posts that already carry an
 * in-content Wide Area AI link.
 *
 * Variants map to message-matched wideareaai.com landing pages:
 *   local-models -> /for/local-models
 *   token-costs  -> /for/token-costs
 *   rate-limits  -> /for/rate-limits
 *
 * Keyed by blog slug (filename without .md == /blog/<slug>).
 */
export const WIDEAREAAI_BLOG_FUNNEL: Record<string, WideAreaAIVariant> = {
  // Local-model hardware/perf readers -> serve your local model reliably
  'local-llm-performance-what-to-expect': 'local-models',
  'running-llms-on-apple-silicon-mlx': 'local-models',
  'how-much-vram-to-run-an-llm': 'local-models',

  // Cost/economics readers -> own your baseline, no per-token fees
  'context-length-vram-cost-benchmark': 'token-costs',

  // Rate-limit pain -> route to your own GPU first
  'understanding-ai-coding-rate-limits': 'rate-limits',
};

/** Variant for a blog post, or undefined when the post isn't in the funnel. */
export function getWideAreaAIVariantForBlog(slug: string): WideAreaAIVariant | undefined {
  return WIDEAREAAI_BLOG_FUNNEL[slug];
}
